import { ReloadOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  Descriptions,
  List,
  Row,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import type { TFunction } from 'i18next';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useSystemGetStatus, useSystemReconcileNow } from '@/api/generated/endpoints/system/system';
import type {
  AlertsOut,
  BackupOut,
  QueueOut,
  ReconcileOut,
  SystemStatusOut,
  WorkerOut,
} from '@/api/generated/model';
import { problemMessage } from '@/i18n/problem';
import { formatDecimal } from '@/lib/decimal';
import { invalidatePath } from '@/lib/queryCache';
import { useIsMobile } from '@/lib/useIsMobile';

const DATETIME = 'DD.MM.YYYY HH:mm';
const REFRESH_MS = 30_000;
const PENDING_MS = 3_000;

const BACKUP_COLOR: Record<BackupOut['state'], string> = {
  ok: 'green',
  stale: 'orange',
  failed: 'red',
  missing: 'red',
  not_configured: 'default',
};

const when = (d: string | null | undefined) => (d ? dayjs(d).format(DATETIME) : '—');
/** Дата рядом с меткой: на узком экране переносится целиком, а не по символу. */
const NoWrap = ({ children }: { children: ReactNode }) => (
  <span style={{ whiteSpace: 'nowrap' }}>{children}</span>
);
const mb = (t: TFunction, bytes: number | null | undefined) =>
  bytes == null
    ? '—'
    : t('system.backup.mb', { n: formatDecimal(String(Math.round(bytes / 104_857.6) / 10)) });

function BackupCard({ backup }: { backup: BackupOut }) {
  const { t } = useTranslation();
  return (
    <Card title={t('system.backup.title')} size="small">
      <Space direction="vertical" style={{ width: '100%' }}>
        <Tag color={BACKUP_COLOR[backup.state]}>{t(`system.backup.state.${backup.state}`)}</Tag>
        {backup.state !== 'not_configured' && (
          <Descriptions size="small" column={1}>
            <Descriptions.Item label={t('system.backup.last')}>
              {when(backup.finished_at)}
            </Descriptions.Item>
            <Descriptions.Item label={t('system.backup.size')}>
              {t('system.backup.sizeValue', {
                db: mb(t, backup.dump_bytes),
                upd: mb(t, backup.upd_bytes),
              })}
            </Descriptions.Item>
            {backup.failed_at && (
              <Descriptions.Item label={t('system.backup.failedAt')}>
                <Typography.Text type="danger">{when(backup.failed_at)}</Typography.Text>
              </Descriptions.Item>
            )}
            <Descriptions.Item label={t('system.backup.restoreCheck')}>
              {backup.restore_ok == null ? (
                '—'
              ) : (
                <Space size={4} wrap>
                  <Tag color={backup.restore_ok ? 'green' : 'red'}>
                    {t(backup.restore_ok ? 'system.backup.restoreOk' : 'system.backup.restoreBad')}
                  </Tag>
                  <NoWrap>{when(backup.restore_checked_at)}</NoWrap>
                </Space>
              )}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Space>
    </Card>
  );
}

/** Оповещения на почту (T-142): включены ли, кому, дошло ли последнее письмо. */
function AlertsCard({ alerts }: { alerts: AlertsOut }) {
  const { t } = useTranslation();
  return (
    <Card title={t('system.alerts.title')} size="small">
      <Space direction="vertical" style={{ width: '100%' }}>
        <Tag color={alerts.enabled ? (alerts.last_error ? 'red' : 'green') : 'default'}>
          {t(alerts.enabled ? 'system.alerts.on' : 'system.alerts.off')}
        </Tag>
        {alerts.enabled ? (
          <Descriptions size="small" column={1}>
            <Descriptions.Item label={t('system.alerts.recipients')}>
              <span style={{ wordBreak: 'break-all' }}>{alerts.recipients.join(', ')}</span>
            </Descriptions.Item>
            <Descriptions.Item label={t('system.alerts.lastSent')}>
              {when(alerts.notified_at)}
            </Descriptions.Item>
            {alerts.last_error && (
              <Descriptions.Item label={t('system.alerts.error')}>
                <Typography.Text type="danger">
                  {when(alerts.last_error_at)}: {alerts.last_error}
                </Typography.Text>
              </Descriptions.Item>
            )}
          </Descriptions>
        ) : (
          <Typography.Text type="secondary">{t('system.alerts.offHint')}</Typography.Text>
        )}
      </Space>
    </Card>
  );
}

function jobKind(t: TFunction, kind: string): string {
  return t(`system.jobKind.${kind}`, { defaultValue: kind });
}

function QueueCard({
  workers,
  queue,
  updFailed,
  reportsFailed,
}: {
  workers: WorkerOut[];
  queue: QueueOut;
  updFailed: number;
  reportsFailed: number;
}) {
  const { t } = useTranslation();
  const alive = workers.find((w) => w.alive);
  const lastSeen = workers[0]?.seen_at;
  return (
    <Card title={t('system.queue.title')} size="small">
      <Descriptions size="small" column={1}>
        <Descriptions.Item label={t('system.queue.worker')}>
          {alive ? (
            <Tag color="green">{t('system.queue.workerAlive')}</Tag>
          ) : (
            <Space size={4} wrap>
              <Tag color="red">{t('system.queue.workerDown')}</Tag>
              {lastSeen && <NoWrap>{t('system.queue.lastSeen', { at: when(lastSeen) })}</NoWrap>}
            </Space>
          )}
        </Descriptions.Item>
        <Descriptions.Item label={t('system.queue.waiting')}>
          {queue.queued}
          {queue.oldest_queued_at &&
            ` · ${t('system.queue.since', { at: when(queue.oldest_queued_at) })}`}
        </Descriptions.Item>
        <Descriptions.Item label={t('system.queue.running')}>
          {queue.running}
          {queue.stuck > 0 && (
            <Typography.Text type="danger">
              {' '}
              · {t('system.queue.stuck', { count: queue.stuck })}
            </Typography.Text>
          )}
        </Descriptions.Item>
        <Descriptions.Item label={t('system.queue.failed7d')}>
          {queue.failed_7d > 0 ? (
            <Typography.Text type="danger">{queue.failed_7d}</Typography.Text>
          ) : (
            0
          )}
        </Descriptions.Item>
        <Descriptions.Item label={t('system.queue.updFailed')}>{updFailed}</Descriptions.Item>
        <Descriptions.Item label={t('system.queue.reportsFailed')}>
          {reportsFailed}
        </Descriptions.Item>
      </Descriptions>
      {queue.recent_failed.length > 0 && (
        <List
          size="small"
          header={t('system.queue.recentErrors')}
          dataSource={queue.recent_failed}
          renderItem={(j) => (
            <List.Item>
              <List.Item.Meta
                title={`${jobKind(t, j.kind)} · ${when(j.finished_at)}`}
                description={
                  <Typography.Text type="danger" ellipsis={{ tooltip: j.last_error }}>
                    {j.last_error}
                  </Typography.Text>
                }
              />
            </List.Item>
          )}
        />
      )}
    </Card>
  );
}

interface Detail {
  key: number;
  text: string;
}

const str = (v: unknown) => (typeof v === 'string' || typeof v === 'number' ? String(v) : '');

/** Строка расхождения для людей: что на складе и что должно быть по журналу/резерву. */
function describe(t: TFunction, d: Record<string, unknown>): string {
  if (d.kind === 'ledger') {
    return t('system.reconcile.ledgerIssue', {
      batch: str(d.batch_id),
      available: formatDecimal(str(d.qty_available)),
      ledger: formatDecimal(str(d.ledger_total)),
    });
  }
  return t('system.reconcile.allocationIssue', {
    number: str(d.shipment_number),
    status: t(`status.shipment.${str(d.status)}.label`),
    line: str(d.line_no),
    qty: formatDecimal(str(d.qty)),
    allocated: formatDecimal(str(d.allocated)),
  });
}

function ReconcileCard({
  run,
  pending,
}: {
  run: ReconcileOut | null | undefined;
  pending: boolean;
}) {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const start = useSystemReconcileNow();

  async function check() {
    try {
      const res = await start.mutateAsync();
      void message.info(t(res.queued ? 'system.reconcile.queued' : 'system.reconcile.already'));
      await invalidatePath(queryClient, '/api/v1/system/status');
    } catch (e) {
      void message.error(problemMessage(e));
    }
  }

  const issues = run ? run.ledger_issues + run.allocation_issues : 0;
  const details: Detail[] = (run?.details ?? []).map((d, key) => ({ key, text: describe(t, d) }));
  return (
    <Card
      title={t('system.reconcile.title')}
      size="small"
      extra={
        <Button
          size="small"
          icon={<SafetyCertificateOutlined />}
          loading={pending || start.isPending}
          onClick={() => void check()}
        >
          {pending ? t('system.reconcile.running') : t('system.reconcile.checkNow')}
        </Button>
      }
    >
      {run ? (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Space wrap>
            {run.ok ? (
              <Tag color="green">{t('system.reconcile.ok')}</Tag>
            ) : (
              <Tag color="red">{t('system.reconcile.issues', { count: issues })}</Tag>
            )}
            <Typography.Text type="secondary">
              {t(`system.reconcile.trigger.${run.trigger}`, { at: when(run.finished_at) })}
            </Typography.Text>
          </Space>
          {!run.ok && (
            <>
              <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
                {t('system.reconcile.hint')}
              </Typography.Paragraph>
              <Table<Detail>
                size="small"
                rowKey="key"
                showHeader={false}
                pagination={details.length > 10 ? { pageSize: 10 } : false}
                dataSource={details}
                columns={[{ key: 'text', dataIndex: 'text' }]}
              />
            </>
          )}
        </Space>
      ) : (
        <Typography.Text type="secondary">{t('system.reconcile.never')}</Typography.Text>
      )}
    </Card>
  );
}

/** «Проверено в … · версия …»: коммит автовыкладки (T-149) — 7 знаков, как в GitHub. */
function checkedLine(t: TFunction, data: SystemStatusOut): string {
  const version = /^[0-9a-f]{40}$/.test(data.version) ? data.version.slice(0, 7) : data.version;
  return `${t('system.checkedAt', { at: dayjs(data.checked_at).format('HH:mm:ss') })} · ${t('system.version', { version })}`;
}

/** Состояние системы: можно ли доверять складу прямо сейчас. */
export function SystemPage() {
  const { t } = useTranslation();
  const mobile = useIsMobile();
  const status = useSystemGetStatus({
    query: {
      refetchInterval: (q) => (q.state.data?.reconcile_pending ? PENDING_MS : REFRESH_MS),
    },
  });
  const data = status.data;

  return (
    <Card
      title={t('system.title')}
      loading={status.isPending}
      extra={
        <Space>
          {data && !mobile && (
            <Typography.Text type="secondary">{checkedLine(t, data)}</Typography.Text>
          )}
          <Button
            icon={<ReloadOutlined />}
            loading={status.isFetching}
            aria-label={t('system.refresh')}
            onClick={() => void status.refetch()}
          >
            {/* Телефон (T-177): в заголовке тесно — кнопка значком, время проверки — под заголовком. */}
            {!mobile && t('system.refresh')}
          </Button>
        </Space>
      }
    >
      {status.isError && <Alert type="error" showIcon message={problemMessage(status.error)} />}
      {data && (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {mobile && <Typography.Text type="secondary">{checkedLine(t, data)}</Typography.Text>}
          {data.ok ? (
            <Alert type="success" showIcon message={t('system.allOk')} />
          ) : (
            <Alert
              type="warning"
              showIcon
              message={t('system.hasProblems')}
              description={
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {data.problems.map((p) => (
                    <li key={p}>{t(`system.problem.${p}`)}</li>
                  ))}
                </ul>
              }
            />
          )}
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={8}>
              <BackupCard backup={data.backup} />
            </Col>
            <Col xs={24} lg={8}>
              <QueueCard
                workers={data.workers}
                queue={data.queue}
                updFailed={data.upd_failed}
                reportsFailed={data.reports_failed_7d}
              />
            </Col>
            <Col xs={24} lg={8}>
              <ReconcileCard run={data.reconcile} pending={data.reconcile_pending} />
            </Col>
            <Col xs={24} lg={8}>
              <AlertsCard alerts={data.alerts} />
            </Col>
          </Row>
        </Space>
      )}
    </Card>
  );
}
